import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  ShieldCheck, Clock, Users, Star, FileText, AlertTriangle,
  CheckCircle2, Check, ArrowRight, Phone, BarChart3, UserCheck,
  Bell, ClipboardList, Banknote, XCircle, ChevronRight, Menu, X, Loader2,
  Lock, Mail, ArrowUpRight, BookOpen, ChevronDown, Sparkles, Briefcase, Eye,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import WhatsAppButton from '../components/WhatsAppButton';
import PricingCards from '../components/Pricing/PricingCards';
import { trackEvent } from '../lib/analytics';

// Phase 2 UX (Feb 2026): public header limited to the 3 essentials.
// Longer content (About / Problem / Solution / Trust / Early Access) still
// lives lower on the page — links to those anchors are removed from the
// header nav so they don't compete with the primary conversion actions.
// Phase 2 UX (Feb 2026): FAQ link removed from the header to reduce clutter.
// The FAQ section still lives lower on the page; primary "Sign in" is
// promoted into the top nav so returning users can jump straight to auth.
const NAV_LINKS = [
  { href: '#how-it-works', label: 'How it works' },
  { href: '#pricing', label: 'Pricing' },
  { href: '/login', label: 'Sign in', route: true },
];


export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);

  const scrollTo = (id) => {
    setMobileNav(false);
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Lock body scroll while the mobile menu overlay is open.
  useEffect(() => {
    if (mobileNav) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileNav]);

  // Fire-once 'homepage_visit' event per browser-session for funnel tracking.
  useEffect(() => {
    trackEvent('homepage_visit', { path: '/' });
  }, []);

  const goToPilotRegister = (source) => {
    trackEvent('start_free_trial_clicked', { source });
    navigate('/register?role=manager&plan=pilot');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* ===== STICKY NAV ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B132B]/95 backdrop-blur-xl border-b border-white/10 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.6)]" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} data-testid="landing-brand-logo">
            <Logo size="sm" linkTo={null} variant="light" wordmarkClassName="text-white" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(l => (
              <button
                key={l.href}
                onClick={() => l.route ? navigate(l.href) : (l.external ? navigate(l.href) : scrollTo(l.href))}
                className="text-sm font-medium text-slate-300/85 hover:text-white transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {l.label}
              </button>
            ))}
            <Button className="rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white px-6 h-10 text-sm font-semibold" onClick={() => navigate('/login')} data-testid="nav-login-btn">
              Sign In / Sign Up
            </Button>
          </div>
          <button className="md:hidden p-2 text-white h-11 w-11 flex items-center justify-center" onClick={() => setMobileNav(!mobileNav)} data-testid="mobile-menu-btn" aria-label="Toggle navigation menu">
            {mobileNav ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileNav && (
          <div
            className="md:hidden fixed inset-0 top-16 bg-[#0B132B] overflow-y-auto"
            style={{ height: 'calc(100vh - 4rem)', height: 'calc(100dvh - 4rem)' }}
            data-testid="mobile-nav-overlay"
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              {NAV_LINKS.map(l => (
                <button
                  key={l.href}
                  onClick={() => {
                    setMobileNav(false);
                    if (l.route) navigate(l.href);
                    else if (l.external) navigate(l.href);
                    else scrollTo(l.href);
                  }}
                  className="block w-full text-left py-3 px-2 text-white/90 hover:text-white font-medium transition-colors border-b border-white/5 min-h-[44px]"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.label}
                </button>
              ))}
              <Button
                className="w-full rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white h-12 mt-4 font-semibold"
                onClick={() => { setMobileNav(false); navigate('/login'); }}
                data-testid="mobile-nav-signin-btn"
              >
                Sign In / Sign Up
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* ===== HERO ===== */}
      <section className="pt-28 sm:pt-32 pb-10 sm:pb-14 px-6" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#4338CA] mb-4">For retailers &amp; customer-facing businesses</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-tight text-slate-900 mb-6" data-testid="hero-headline">
              Need trusted cover for your branch?
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-7 max-w-lg" data-testid="hero-supporting-text">
              Post a shift and find trained, available workers without relying on phone calls and WhatsApp groups.
            </p>

            {/* Phase 2 UX: dual CTAs — one for managers, one for workers.
                Each pre-selects the role so the visitor never has to
                answer "who are you?" twice. */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Button
                className="rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white px-8 h-14 text-base font-semibold shadow-lg shadow-red-200/50 w-full sm:w-auto"
                onClick={() => { trackEvent('hero_cta_manager'); navigate('/register?role=manager'); }}
                data-testid="hero-manager-cta"
              >
                Find Cover <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="rounded-full px-8 h-14 text-base font-semibold border-2 border-slate-300 text-slate-700 hover:bg-slate-50 w-full sm:w-auto"
                onClick={() => { trackEvent('hero_cta_worker'); navigate('/register?role=worker'); }}
                data-testid="hero-worker-cta"
              >
                I Want to Work Shifts
              </Button>
            </div>
            <p className="text-[13px] text-slate-500 mt-4" data-testid="hero-signin-hint">
              Already registered?{' '}
              <button
                type="button"
                className="text-[#4338CA] font-semibold hover:underline"
                onClick={() => navigate('/login')}
                data-testid="hero-signin-link"
              >
                Sign in
              </button>
            </p>
          </div>
          <div className="relative hidden md:block" data-testid="hero-mockup">
            <div className="bg-white rounded-2xl shadow-2xl shadow-slate-200/80 border border-slate-100 p-6">
              <div className="bg-[#0B132B] rounded-xl p-6 text-white">
                <div className="flex items-center gap-3 mb-5">
                  <ShieldCheck className="h-6 w-6 text-[#10B981]" />
                  <span className="font-bold text-lg">Shift Dashboard</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <MockStat label="Active Shifts" value="12" color="text-[#10B981]" />
                  <MockStat label="Workers" value="48" color="text-indigo-400" />
                  <MockStat label="Reliability" value="96%" color="text-amber-400" />
                </div>
                <div className="space-y-2">
                  {['Jamaica Road - Tomorrow 8am', 'High Street - Today 2pm', 'Station Branch - Wed 6am'].map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/10 rounded-lg px-3 py-2">
                      <span className="text-sm text-white/90">{s}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${i === 0 ? 'bg-[#10B981]/20 text-[#10B981]' : i === 1 ? 'bg-amber-400/20 text-amber-400' : 'bg-indigo-400/20 text-indigo-400'}`}>
                        {i === 0 ? 'Filled' : i === 1 ? 'In Progress' : 'Open'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRUST STRIP (beneath hero) ===== */}
      <section className="px-6 pb-14 sm:pb-16" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 sm:px-7 sm:py-5 shadow-sm">
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3 items-center">
              {[
                { icon: Users, label: 'Trusted Staff Network' },
                { icon: Star, label: 'Reliability History' },
                { icon: UserCheck, label: 'Industry-Experienced Workers' },
                { icon: Clock, label: 'Real-Time Availability' },
                { icon: ClipboardList, label: 'Accountability Records' },
              ].map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2.5 min-w-0" data-testid={`trust-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <span className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#0B132B]">
                    <Icon className="h-4.5 w-4.5 text-white" strokeWidth={2.2} />
                  </span>
                  <span className="text-[12.5px] sm:text-[13px] font-semibold text-slate-800 leading-tight truncate">{label}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-500 mt-3 sm:mt-4">
              <span className="font-semibold text-slate-700">ICO Registered</span> · ZC161220 · <span className="font-semibold text-slate-700">UK-Based Support</span> · Operated by Shiftcover365 Ltd · <a href="mailto:support@shiftcover365.co.uk" className="text-[#4338CA] hover:underline">support@shiftcover365.co.uk</a>
            </p>
          </div>
        </div>
      </section>

      {/* ===== SURVEY-LED POSITIONING ===== */}
      <section className="px-6 pb-14 sm:pb-16" data-testid="survey-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white shadow-sm p-6 sm:p-8" data-testid="survey-built-around-card">
            <p className="uppercase tracking-[0.18em] text-[10.5px] font-bold text-[#4338CA] mb-3">Built from real business owner feedback</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B132B] mb-4">
              Built Around What Businesses Told Us
            </h2>
            <p className="text-[14.5px] sm:text-[15px] text-slate-700 leading-relaxed mb-4">
              Before building ShiftCover365, we spoke with business owners about the biggest challenges they face when covering shifts and managing staffing shortages.
            </p>
            <p className="text-[14.5px] sm:text-[15px] text-slate-700 leading-relaxed mb-4">
              The most common responses were:
            </p>
            <ul className="space-y-2.5 mb-5">
              {[
                'Finding trusted staff',
                'Finding trained staff',
                'Real-time availability',
                'Better accountability and visibility',
                'Workers with relevant experience',
                'Faster shift coverage when staff call in sick',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[14.5px] text-slate-800">
                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-[14.5px] sm:text-[15px] text-slate-700 leading-relaxed">
              <strong className="font-semibold text-slate-900">ShiftCover365 was designed around these priorities.</strong>
            </p>
          </div>

          <aside className="lg:col-span-2 rounded-2xl bg-gradient-to-br from-[#0B132B] to-[#1e1a4d] text-white p-6 sm:p-7 shadow-xl shadow-indigo-900/20 relative overflow-hidden" data-testid="why-we-built-callout">
            <div className="absolute inset-0 opacity-[0.07]" style={{
              backgroundImage: 'radial-gradient(circle at 30% 30%, white 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }} />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/10">
                  <Sparkles className="h-4.5 w-4.5 text-amber-300" />
                </span>
                <p className="uppercase tracking-[0.18em] text-[10.5px] font-bold text-amber-300">Why We Built</p>
              </div>
              <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-white mb-3">
                Why We Built ShiftCover365
              </h3>
              <p className="text-[14px] text-white/85 leading-relaxed mb-3">
                Business owners consistently told us that finding <strong className="text-white">trusted, trained staff at short notice</strong> was one of their biggest challenges.
              </p>
              <p className="text-[14px] text-white/85 leading-relaxed">
                ShiftCover365 was built to help solve that problem.
              </p>
            </div>
          </aside>
        </div>
      </section>


      {/* ===== WHY BUSINESSES TRUST SHIFTCOVER365 ===== */}
      <section id="why-trust" className="py-16 sm:py-20 px-6 bg-white" data-testid="why-businesses-trust">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#4338CA] mb-3">Built on trust</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B132B] mb-3">
              Why Businesses Trust ShiftCover365
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Six checks and signals on every worker, so the only decision left is whether they&apos;re right for your branch.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: UserCheck,     label: 'Identity verified workers' },
              { icon: Star,          label: 'Reliability history visible before approval' },
              { icon: Briefcase,     label: 'Industry experience displayed on profiles' },
              { icon: Clock,         label: 'Real-time availability' },
              { icon: Eye,           label: 'Transparent ratings and feedback' },
              { icon: ClipboardList, label: 'Clear accountability records' },
            ].map(({ icon: Icon, label }, idx) => (
              <div
                key={label}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all flex items-start gap-3"
                data-testid={`why-trust-item-${idx + 1}`}
              >
                <span className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#0B132B] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.1} />
                </span>
                <p className="text-[14.5px] font-semibold text-[#0B132B] leading-snug pt-1.5">{label}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-[12.5px] text-slate-500 mt-7">
            Want the full verification process? <a href="/learn-more#trust-and-verification" className="text-[#4338CA] hover:underline font-semibold">See how we verify workers on the Learn More page →</a>
          </p>
        </div>
      </section>

      {/* ===== FOUNDER ===== */}
      <section id="founder" className="py-16 sm:py-20 px-6 bg-[#0B132B] text-white" data-testid="founder-section">
        <div className="max-w-5xl mx-auto grid md:grid-cols-5 gap-8 sm:gap-10 items-center">
          <div className="md:col-span-2">
            <div
              className="aspect-square w-full max-w-xs mx-auto md:mx-0 rounded-2xl overflow-hidden shadow-2xl shadow-red-900/40 ring-4 ring-white/10"
              data-testid="founder-photo"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_c81cb521-10eb-499a-aced-0c520ab93005/artifacts/gc6dgzwb_image0.jpeg"
                alt="Alia Veerasawmy, Founder &amp; CEO of ShiftCover365"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-red-400 mb-3">Meet the founder</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-2" data-testid="founder-name">
              Alia Veerasawmy
            </h2>
            <p className="text-sm text-white/60 mb-5">Founder &amp; CEO, ShiftCover365 Ltd</p>
            <div className="space-y-4 text-[15px] text-white/85 leading-relaxed">
              <p>
                I started ShiftCover365 after a chance conversation inside a small Post Office. The Postmaster was working the counter alone — no cover, no break, no easy way to find a trusted hand at short notice.
              </p>
              <p>
                That moment stuck with me. I knew there had to be a smarter way for Postmasters to find reliable cover, protect their tills, and reclaim their time.
              </p>
              <p className="text-white font-semibold">
                ShiftCover365 exists to give branch owners back their freedom — with the accountability and trust they deserve.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="mailto:support@shiftcover365.co.uk"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#0B132B] px-4 h-10 text-sm font-semibold hover:bg-white/90 transition-colors"
                data-testid="founder-email-link"
              >
                <Mail className="h-4 w-4" /> Email the founder
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS (placeholders, ready for real Postmaster quotes) - HIDDEN until real quotes are available ===== */}
      <section id="testimonials" className="hidden py-16 sm:py-20 px-6 bg-white" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#DA291C] mb-3">From Postmasters</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B132B] mb-3">
              Trusted by branches like yours
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Real quotes from real Postmasters in our pilot — coming soon.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { initials: 'AP', name: 'Postmaster name', branch: 'Branch name', city: 'City', quote: '“Quote from a pilot Postmaster will appear here after their first 30 days.”' },
              { initials: 'MR', name: 'Postmaster name', branch: 'Branch name', city: 'City', quote: '“Quote from a pilot Postmaster will appear here after their first 30 days.”' },
              { initials: 'JS', name: 'Postmaster name', branch: 'Branch name', city: 'City', quote: '“Quote from a pilot Postmaster will appear here after their first 30 days.”' },
            ].map((t, i) => (
              <figure
                key={i}
                className="rounded-2xl border border-dashed border-slate-300 bg-[#F8FAFC] p-6 relative"
                data-testid={`testimonial-placeholder-${i + 1}`}
              >
                <span className="absolute top-3 right-3 text-[9.5px] uppercase tracking-[0.18em] font-bold text-slate-400">Placeholder</span>
                <svg className="h-5 w-5 text-[#DA291C] mb-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
                </svg>
                <blockquote className="text-[14.5px] text-slate-700 leading-relaxed italic mb-5">
                  {t.quote}
                </blockquote>
                <figcaption className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-slate-200 text-slate-500 text-xs font-bold tracking-wider">
                    {t.initials}
                  </span>
                  <div className="text-[12px] leading-tight">
                    <p className="font-semibold text-slate-900">{t.name}</p>
                    <p className="text-slate-500">{t.branch} · {t.city}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          <p className="text-center text-[12px] text-slate-400 mt-6">
            Are you a pilot Postmaster? Email <a className="text-[#4338CA] hover:underline" href="mailto:support@shiftcover365.co.uk">support@shiftcover365.co.uk</a> with a short quote and we&apos;ll feature you here.
          </p>
        </div>
      </section>

      {/* ===== ABOUT SHIFTCOVER365 ===== */}
      <section id="about" className="py-20 px-6 bg-white" data-testid="about-section">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-14">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#DA291C] mb-4">About</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0B132B]">
              About ShiftCover365
            </h2>
          </div>

          {/* 1. About */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start mb-20" data-testid="about-block-about">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#0B132B] mb-5">About ShiftCover365</h3>
              <p className="text-base text-slate-700 leading-relaxed mb-4">
                ShiftCover365 was created to solve one of the most overlooked challenges faced by Post Office branches — finding reliable, trusted staff cover at short notice while keeping tills and operations secure.
              </p>
              <p className="text-base text-slate-700 leading-relaxed">
                We’re redefining how branch owners manage absences, accountability, and trust.
              </p>
            </div>
            <div className="rounded-3xl bg-[#0B132B] text-white p-8 sm:p-10 shadow-xl shadow-slate-900/10 relative overflow-hidden" data-testid="about-mission-card">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#DA291C]/10 rounded-full -mr-12 -mt-12" aria-hidden />
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-red-300 mb-3">Our Mission</p>
              <p className="text-lg sm:text-xl font-medium leading-relaxed">
                Our mission is simple: to help Postmasters maintain business continuity, protect their revenue, and ensure peace of mind — every single day of the year.
              </p>
            </div>
          </div>

          {/* 2. Our Story */}
          <div className="mb-20" data-testid="about-block-story">
            <div className="rounded-3xl bg-[#F3F4F6] p-8 sm:p-12 lg:p-16">
              <h3 className="text-2xl sm:text-3xl font-bold text-[#0B132B] mb-6">Our Story</h3>
              <div className="space-y-5 text-base text-slate-700 leading-relaxed max-w-3xl">
                <p>The idea for ShiftCover365 started on an ordinary day inside a small Post Office.</p>
                <p>I walked in to post a parcel and noticed there wasn’t anyone there except the Postmaster, working alone behind the counter.</p>
                <p>We started talking, and they shared the challenges they face every day — finding reliable cover when staff call in sick, struggling to take time off, and feeling tied to the branch around the clock.</p>
                <p className="font-semibold text-[#0B132B]">That moment sparked something.</p>
                <p>I realised there needed to be a smarter way for Postmasters to find trusted cover, protect their tills, and run their businesses with peace of mind.</p>
              </div>

              {/* Quote card */}
              <div className="mt-8 sm:mt-10 rounded-2xl bg-white border-l-4 border-[#DA291C] p-6 sm:p-8 shadow-sm" data-testid="about-story-quote">
                <svg className="h-6 w-6 text-[#DA291C] mb-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z"/>
                </svg>
                <p className="text-lg sm:text-xl font-medium text-[#0B132B] leading-relaxed italic">
                  If I were a business owner, I’d want to know I have the freedom to step away when I need to — without worrying. I’d expect that freedom, and the financial stability that comes with it.
                </p>
              </div>

              <p className="mt-8 text-base text-slate-700 leading-relaxed max-w-3xl">
                That’s how ShiftCover365 was born — from one conversation, one observation, and one mission:
                <span className="font-semibold text-[#0B132B]"> to give Postmasters back their time, their trust, and their freedom.</span>
              </p>
            </div>
          </div>

          {/* 3. Our Vision */}
          <div data-testid="about-block-vision">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#0B132B] mb-5">Our Vision</h3>
                <p className="text-base text-slate-700 leading-relaxed mb-4">
                  ShiftCover365’s vision is to become the most trusted shift-management and accountability platform across the UK’s retail and postal network.
                </p>
                <p className="text-base text-slate-700 leading-relaxed mb-4">
                  We aim to give Postmasters — and eventually other local business owners — the confidence to step away from their business knowing that every shift, every till, and every transaction is protected.
                </p>
                <p className="text-base text-slate-700 leading-relaxed">
                  By blending smart technology with human accountability, ShiftCover365 helps businesses run smoother, safer, and smarter — <span className="font-semibold text-[#0B132B]">365 days a year</span>.
                </p>
              </div>
              <div className="rounded-3xl bg-gradient-to-br from-[#DA291C] to-[#a8190f] text-white p-8 sm:p-10 shadow-xl shadow-red-200/40 relative overflow-hidden" data-testid="about-vision-quote">
                <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/5 rounded-full" aria-hidden />
                <svg className="h-7 w-7 text-white/70 mb-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z"/>
                </svg>
                <p className="text-xl sm:text-2xl font-semibold leading-snug">
                  At ShiftCover365, we’re not just covering shifts — we’re protecting trust, one branch at a time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROBLEM ===== */}
      <section id="problem" className="py-20 px-6 bg-[#0B132B]" data-testid="problem-section">
        <div className="max-w-5xl mx-auto text-center">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-red-400 mb-4">The Real Problem</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6">
            Why WhatsApp Groups and Calling Around Stop Working
          </h2>
          <p className="text-lg text-slate-400 mb-12 max-w-2xl mx-auto">
            Every branch knows the stress. These are the problems you deal with every week.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Users, title: 'Finding trusted staff', desc: 'No reliable way to find verified workers quickly' },
              { icon: XCircle, title: 'Last-minute cancellations', desc: 'Workers cancel with no accountability or consequence' },
              { icon: Phone, title: 'Time wasted calling around', desc: 'Hours spent on WhatsApp trying to find cover' },
              { icon: Bell, title: 'Emergency blind spots', desc: 'No one to contact when things go wrong mid-shift' },
              { icon: Banknote, title: 'Shortage accountability', desc: 'No audit trail when the till is short after cover' },
              { icon: FileText, title: 'No proper records', desc: 'No history of who covered, when, and what happened' },
            ].map((item, i) => (
              <div key={i} className="text-left p-6 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                <item.icon className="h-8 w-8 text-red-400 mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SOLUTION ===== */}
      <section id="solution" className="py-20 px-6 bg-[#F3F4F6]" data-testid="solution-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#4338CA] mb-4">The Solution</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              A Better Way To Manage Cover
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Clock, title: 'Post & Fill Quickly', desc: 'Create a shift, get applicants, fill it in minutes.' },
              { icon: ShieldCheck, title: 'Worker Verification', desc: 'Smart ID and HR checks before anyone starts work.' },
              { icon: Star, title: 'Ratings & Reviews', desc: 'Rate workers after shifts. Build trust over time.' },
              { icon: FileText, title: 'Shift Audit Trail', desc: 'Complete history of every covered shift.' },
              { icon: Banknote, title: 'Till Accountability', desc: 'Track till amounts start to finish with discrepancy alerts.' },
              { icon: UserCheck, title: 'HR Verification', desc: 'Manager confirms Smart ID with HR support.' },
              { icon: Bell, title: 'Real-time Notifications', desc: 'Instant alerts for applications, discrepancies, and issues.' },
              { icon: BarChart3, title: 'Reliability History', desc: 'See cancellations, no-shows, and trust scores at a glance.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200/80 hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
                <div className="h-12 w-12 rounded-xl bg-[#4338CA]/10 flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-[#4338CA]" />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ACCOUNTABILITY ===== */}
      <section className="py-20 px-6" data-testid="accountability-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#10B981] mb-4">Core Principle</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              Built For Accountability, Not Just Availability
            </h2>
          </div>
          <div className="grid md:grid-cols-5 gap-px bg-slate-200 rounded-2xl overflow-hidden">
            {[
              { icon: AlertTriangle, title: 'Track Cancellations', desc: 'Every cancellation and no-show is recorded' },
              { icon: ClipboardList, title: 'Worker History', desc: 'Full behaviour profile for every worker' },
              { icon: Banknote, title: 'Till Monitoring', desc: 'Start, mid-shift, and end till tracking' },
              { icon: FileText, title: 'Shift Records', desc: 'Complete record of every covered shift' },
              { icon: ShieldCheck, title: 'Branch Verification', desc: 'Verification persists across the platform' },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 text-center">
                <item.icon className="h-8 w-8 text-[#10B981] mx-auto mb-3" />
                <h3 className="text-sm font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-20 px-6 bg-[#0B132B]" data-testid="how-it-works-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-indigo-400 mb-4">Simple Process</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-14">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: '01', title: 'Post a Shift', desc: 'Create a shift with location, time, and requirements. Workers are notified instantly.' },
              { num: '02', title: 'Review & Verify', desc: 'See applicant profiles, ratings, and Smart ID status. Verify trust before approving.' },
              { num: '03', title: 'Full Accountability', desc: 'Track the shift from start to end. Till checks, time logs, and complete audit trail.' },
            ].map((step, i) => (
              <div key={i} className="text-left">
                <span className="text-6xl font-extrabold text-white/10 block mb-3">{step.num}</span>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHO IT'S FOR ===== */}
      <section className="py-20 px-6" data-testid="who-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#4338CA] mb-4">Who It's For</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Built For Teams That Need Trust
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, title: 'Post Office Branches', desc: 'Fill shifts with verified, trusted workers.' },
              { icon: Users, title: 'Retail Businesses', desc: 'Reliable internal cover with accountability.' },
              { icon: ClipboardList, title: 'Operations Managers', desc: 'Full audit trail and compliance visibility.' },
              { icon: BarChart3, title: 'Branch Networks', desc: 'Cross-branch verification and trust.' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-shadow">
                <item.icon className="h-8 w-8 text-[#4338CA] mb-3" />
                <h3 className="text-base font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROOF OF CONCEPT NOTE ===== */}
      <section className="px-6 py-10 sm:py-12 bg-slate-50 border-y border-slate-200" data-testid="proof-of-concept-section">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[13.5px] sm:text-sm text-slate-600 leading-relaxed">
            <span className="inline-block px-2.5 py-0.5 rounded-full bg-slate-200/80 text-[10.5px] uppercase tracking-[0.12em] font-bold text-slate-700 mr-2 align-middle">Origin</span>
            ShiftCover365 was initially developed alongside Post Office branches and is now expanding to support retailers and customer-facing businesses across the UK.
          </p>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 px-6 bg-[#4338CA]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4">
            Stop Chasing Cover.<br />Start Managing It Properly.
          </h2>
          <p className="text-lg text-indigo-200 mb-8">Join branches that are building trust into every shift.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              className="rounded-full bg-white text-[#4338CA] hover:bg-slate-100 px-8 h-14 text-base font-bold shadow-lg"
              onClick={() => goToPilotRegister('cta-band-primary')}
              data-testid="cta-start-pilot-btn"
            >
              Start Free Trial <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-2 border-white text-white hover:bg-white/10 px-8 h-14 text-base font-bold"
              onClick={() => goToPilotRegister('cta-band-secondary')}
              data-testid="cta-try-free-btn"
            >
              Try ShiftCover365 Free
            </Button>
          </div>
        </div>
      </section>


      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-20 px-6 bg-[#F3F4F6]" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10.5px] font-bold uppercase tracking-[0.18em] mb-4">
              <Sparkles className="h-3.5 w-3.5" /> 14-day free trial for every manager
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B132B] mb-3">
              Simple, honest pricing
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Start free. Add a card any time in your first 14 days — or roll into Pay-As-You-Go on day 15 and only pay when you actually cover a shift.
            </p>
          </div>

          <PricingCards onCtaClick={() => goToPilotRegister('pricing-section')} />
        </div>
      </section>


      {/* ===== CTA — Book a demo OR start free trial ===== */}
      <section id="get-started" className="py-20 px-6" data-testid="get-started-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B132B] mb-3">
              Ready to see it in action?
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Chat with the founder for 20 minutes, or start your 14-day free trial right now — no card needed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 items-stretch">
            {/* Option 1 — Book a Demo (secondary) */}
            <div
              className="rounded-3xl bg-white border border-slate-200 shadow-sm p-7 sm:p-8 flex flex-col"
              data-testid="option-book-demo-card"
            >
              <p className="uppercase tracking-[0.18em] text-[10.5px] font-bold text-slate-500 mb-3">Option 1</p>
              <h3 className="text-2xl sm:text-[26px] font-extrabold tracking-tight text-[#0B132B] mb-3">
                Book a Demo
              </h3>
              <p className="text-[14.5px] text-slate-600 leading-relaxed mb-5 flex-1">
                See ShiftCover365 in action and discuss your staffing challenges with the founder.
              </p>
              <ul className="space-y-2 mb-6 text-[13px] text-slate-600">
                {['20-minute walkthrough', 'Speak with the founder directly', 'No card, no pressure'].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> <span>{b}</span>
                  </li>
                ))}
              </ul>
              <a
                href="mailto:support@shiftcover365.co.uk?subject=Demo%20request%20%E2%80%94%20ShiftCover365&body=Hi%20ShiftCover365%2C%0A%0AI%27d%20like%20to%20request%20a%20demo.%0A%0AMy%20business%3A%0ANumber%20of%20locations%3A%0APreferred%20day%2Ftime%20for%20the%20call%3A%0A%0AThanks%2C"
                className="inline-flex items-center justify-center gap-2 w-full rounded-full bg-white text-[#0B132B] border-2 border-[#0B132B] hover:bg-[#0B132B] hover:text-white h-12 text-sm font-bold transition-colors"
                data-testid="book-demo-btn"
                onClick={() => trackEvent('demo_requested', { source: 'option-book-demo' })}
              >
                Book a Demo <ArrowRight className="h-4 w-4" />
              </a>
              <p className="text-[11px] text-slate-500 text-center mt-3">Prefer email? <a href="mailto:support@shiftcover365.co.uk" className="underline hover:text-slate-700">support@shiftcover365.co.uk</a></p>
            </div>

            {/* Option 2 — Start Free Trial (primary) */}
            <div
              className="rounded-3xl bg-gradient-to-br from-[#DA291C] to-[#7a160c] text-white p-7 sm:p-8 shadow-xl shadow-red-900/30 flex flex-col relative overflow-hidden"
              data-testid="option-sign-up-card"
            >
              <div className="absolute inset-0 opacity-[0.08]" style={{
                backgroundImage: 'radial-gradient(circle at 30% 30%, white 1px, transparent 1px)',
                backgroundSize: '28px 28px',
              }} />
              <div className="relative flex flex-col flex-1">
                <div className="flex items-center justify-between mb-3">
                  <p className="uppercase tracking-[0.18em] text-[10.5px] font-bold text-amber-200">Option 2</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-300/95 text-[#7a160c] text-[10px] font-bold uppercase tracking-[0.12em]">Recommended</span>
                </div>
                <h3 className="text-2xl sm:text-[26px] font-extrabold tracking-tight mb-3">
                  Start Your 14-Day Free Trial
                </h3>
                <p className="text-[14.5px] text-white/90 leading-relaxed mb-5 flex-1">
                  Create your account and explore ShiftCover365 in under 2 minutes.
                </p>
                <ul className="space-y-2 mb-6 text-[13px] text-white/85">
                  {['No card required', 'Instant access to the dashboard', 'Cancel any time before the trial ends'].map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" /> <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => goToPilotRegister('option-sign-up')}
                  className="inline-flex items-center justify-center gap-2 w-full rounded-full bg-white text-[#DA291C] hover:bg-amber-50 h-13 sm:h-14 text-[15px] font-extrabold shadow-lg shadow-red-900/40 transition-colors"
                  data-testid="sign-up-today-btn"
                  style={{ paddingTop: 0, paddingBottom: 0 }}
                >
                  Start My Free Trial <ArrowRight className="h-5 w-5" />
                </button>
                <p className="text-[11px] text-white/65 text-center mt-3">No card required to start. Add payment before day 14 to keep using paid plans — otherwise roll into Pay-As-You-Go automatically.</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ===== FAQ ===== */}
      <section id="faq" className="py-20 px-6" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#4338CA] mb-4">Questions</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Frequently Asked Questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              { q: 'How is this different from a WhatsApp group?', a: 'ShiftCover365 gives you verified workers, accountability tracking, till auditing, ratings, and a complete audit trail. WhatsApp has none of that. Every action is logged and reviewable.' },
              { q: 'Can I verify workers before assigning shifts?', a: 'Yes. Every applicant shows their Smart ID status, HR verification history, rating, reliability score, cancellation count, and no-show record. You can also call HR to confirm Smart ID directly from the app.' },
              { q: 'Can I track cancellations and no-shows?', a: 'Absolutely. Every cancellation and no-show is recorded against the worker\'s profile. Managers see behaviour counts and patterns. Late cancellations are flagged separately.' },
              { q: 'Is there a record of shortages or till issues?', a: 'Yes. Workers record starting till, periodic checks, and end-of-shift amounts. Any discrepancy triggers an immediate notification to the manager with a full audit trail.' },
              { q: 'How does the free trial work?', a: 'Every new manager gets a 14-day free trial the moment they register — no card required. Add your payment details any time before day 14 to keep using paid plans; if you don\u2019t add a card, we\u2019ll automatically move you to Pay-As-You-Go on day 15 so you only pay when you actually cover a shift.' },
              { q: 'What are the paid plans?', a: 'Pay-As-You-Go at \u00a33.99 per successfully completed shift, billed monthly. Monthly at \u00a329.99/location, Annual at \u00a3290/location (~\u00a324.17/month equivalent), and Multi-site at \u00a399/month for up to 5 locations. The first 20 Monthly customers lock in a Founding Member rate of \u00a319.99/month for as long as their subscription stays active.' },
            ].map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border border-slate-200 px-6 overflow-hidden">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="relative bg-[#0B132B] text-slate-300 overflow-hidden" data-testid="footer">
        {/* subtle gradient lighting + grid backdrop */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 left-1/4 h-64 w-[600px] rounded-full bg-[#4338CA]/10 blur-3xl" />
          <div className="absolute -bottom-32 right-0 h-72 w-[480px] rounded-full bg-[#DA291C]/8 blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,rgba(255,255,255,0.02)_100%)]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 pt-10 pb-6">
          {/* Main columns */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 mb-8">
            {/* Branding column */}
            <div className="md:col-span-5">
              <div className="mb-5" data-testid="landing-footer-logo">
                <Logo size="md" linkTo={null} variant="light" />
              </div>
              <p className="text-[15px] sm:text-base text-slate-100 leading-snug font-semibold max-w-md tracking-tight">
                Trusted workforce infrastructure for modern shift-based businesses.
              </p>
              <p className="text-[13px] text-slate-300/80 leading-relaxed mt-2 max-w-md">
                Built for Post Offices, retail branches, and shift-based operations.
              </p>
              <p className="text-[12px] text-slate-400/60 leading-relaxed mt-3 max-w-md">
                Built for accountability, reliability, and operational visibility.
              </p>

              {/* Social row */}
              <div className="flex items-center gap-2 mt-6">
                <FooterSocial href="mailto:hello@shiftcover365.co.uk" label="Email us" icon={Mail} />
              </div>
            </div>

            {/* Platform */}
            <div className="md:col-span-3">
              <h4 className="text-[10.5px] font-semibold text-white/45 uppercase tracking-[0.14em] mb-4">Platform</h4>
              <ul className="space-y-2.5">
                {[
                  { label: 'About', href: '#about' },
                  { label: 'Features', href: '#solution' },
                  { label: 'How It Works', href: '#how-it-works' },
                  { label: 'FAQ', href: '#faq' },
                ].map((l) => (
                  <li key={l.href}>
                    <button
                      onClick={() => scrollTo(l.href)}
                      className="text-[13.5px] text-slate-300/85 hover:text-white transition-colors"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust & Legal — split into two clear sub-sections */}
            <div className="md:col-span-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                {/* Trust column — static indicators, not clickable */}
                <div>
                  <h4 className="text-[10.5px] font-semibold text-white/45 uppercase tracking-[0.14em] mb-4">Trust</h4>
                  <ul className="space-y-2.5" data-testid="footer-trust-list">
                    {[
                      { label: 'GDPR compliant', icon: ShieldCheck },
                      { label: 'Secure authentication', icon: Lock },
                      { label: 'Verified workforce', icon: UserCheck },
                    ].map((t) => {
                      const I = t.icon;
                      return (
                        <li
                          key={t.label}
                          className="flex items-center gap-2 text-[12.5px] text-slate-400/70 cursor-default select-none"
                        >
                          <I className="h-3.5 w-3.5 text-slate-400/60 shrink-0" aria-hidden="true" />
                          <span>{t.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Legal column */}
                <div>
                  <h4 className="text-[10.5px] font-semibold text-white/45 uppercase tracking-[0.14em] mb-4">Legal</h4>
                  <ul className="space-y-2.5">
                    {[
                      { label: 'Privacy Policy', href: '/privacy' },
                      { label: 'Terms & Conditions', href: '/terms' },
                      { label: 'Cookie Policy', href: '/cookies' },
                      { label: 'Contact Us', href: 'mailto:hello@shiftcover365.co.uk', external: true },
                      { label: 'Account Deletion', href: '/account-deletion' },
                      { label: 'Trust Center', href: '/trust' },
                    ].map((l) => (
                      <li key={l.href}>
                        {l.external ? (
                          <a
                            href={l.href}
                            data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                            className="group inline-flex items-center text-[13px] text-slate-300/85 hover:text-white transition-colors"
                          >
                            <span className="border-b border-transparent group-hover:border-white/40 transition-colors pb-px">
                              {l.label}
                            </span>
                          </a>
                        ) : (
                          <Link
                            to={l.href}
                            data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                            className="group inline-flex items-center text-[13px] text-slate-300/85 hover:text-white transition-colors"
                          >
                            <span className="border-b border-transparent group-hover:border-white/40 transition-colors pb-px">
                              {l.label}
                            </span>
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Divider + bottom row */}
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-[12px] text-slate-400/70">
              © {new Date().getFullYear()} ShiftCover365 · All rights reserved.
            </p>
            <div className="flex items-center gap-1.5 text-[11.5px] text-slate-400/60">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              All systems operational
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp contact — hidden if REACT_APP_WHATSAPP_NUMBER unset */}
      <WhatsAppButton />
    </div>
  );
}

function FooterSocial({ href, label, icon: Icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="group inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/[0.05] border border-white/10 text-slate-300 hover:bg-white/[0.10] hover:text-white hover:border-white/20 transition-all duration-200"
    >
      <Icon className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
    </a>
  );
}

function MockStat({ label, value, color }) {
  return (
    <div className="bg-white/5 rounded-lg p-3 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-slate-400">{label}</p>
    </div>
  );
}

function ProofCard() { return null; }
// (Removed — replaced by single-line social-proof copy.)
